import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Users, Pizza, Wand2, Clock, Shield, Camera, Palette, PartyPopper, Gift, Cake, Heart, Play, X, ChevronLeft, ChevronRight, Quote, Check, UserCheck } from 'lucide-react';

// Real photos & video clips from a birthday party at Immersa (faces blurred, shared with the family's permission)
type GalleryItem = {
  type: 'image' | 'video';
  src: string;
  poster?: string;
  caption: string;
};

const galleryItems: GalleryItem[] = [
  { type: 'image', src: '/assets/BirthdayParties/party-table-decor.jpeg', caption: 'A galaxy-themed table, set up just for the guest of honor' },
  { type: 'video', src: '/assets/BirthdayParties/party-clip-5.mp4', poster: '/assets/BirthdayParties/party-clip-5-poster.jpg', caption: 'Diving into VR — whole new worlds at their fingertips 🎮' },
  { type: 'image', src: '/assets/BirthdayParties/party-table-wide.jpeg', caption: 'The whole crew together — a private table, all yours' },
  { type: 'video', src: '/assets/BirthdayParties/party-clip-3.mp4', poster: '/assets/BirthdayParties/party-clip-3-poster.jpg', caption: 'Cake, candles & the big birthday moment 🎂' },
  { type: 'video', src: '/assets/BirthdayParties/party-clip-4.mp4', poster: '/assets/BirthdayParties/party-clip-4-poster.jpg', caption: 'Strapping in for the UFO motion ride 🛸' },
  { type: 'image', src: '/assets/BirthdayParties/vr-arcade-action.jpeg', caption: 'The VR arcade, private and buzzing with energy' },
  { type: 'video', src: '/assets/BirthdayParties/party-clip-1.mp4', poster: '/assets/BirthdayParties/party-clip-1-poster.jpg', caption: 'Host-led games right at the party table' },
  { type: 'image', src: '/assets/BirthdayParties/host-serving.jpeg', caption: 'Our hosts handle every detail so parents can relax' },
  { type: 'video', src: '/assets/BirthdayParties/party-clip-6.mp4', poster: '/assets/BirthdayParties/party-clip-6-poster.jpg', caption: 'Full-speed VR racing 🏎️' },
  { type: 'image', src: '/assets/BirthdayParties/party-table-cupcakes.jpeg', caption: 'Themed décor & sweet treats, all included' },
  { type: 'video', src: '/assets/BirthdayParties/party-clip-2.mp4', poster: '/assets/BirthdayParties/party-clip-2-poster.jpg', caption: 'The whole table erupts in cheers 🎉' },
  { type: 'image', src: '/assets/BirthdayParties/party-gifts-table.jpeg', caption: 'Host-led table games keep every kid in the fun' },
];

// Real 5-star Google reviews for Immersa Edutainment (lightly trimmed; wording unchanged)
const GOOGLE_REVIEWS_URL = 'https://www.google.com/maps?cid=7756319237343928481';

type GoogleReview = { name: string; badge?: string; quote: string };

const googleReviews: GoogleReview[] = [
  { name: 'Tarik Bayu', quote: "Such a great experience for our little one's birthday! Can't wait to return back!" },
  { name: 'Yohannes Mekonnen', quote: 'We celebrated our little one’s birthday here — amazing service and memories.' },
  { name: 'Esther J', badge: 'Local Guide', quote: 'Kids had a great time… They do birthday parties here too, which would be a great option. The guy running the place was super helpful.' },
  { name: 'Lailah Nawaz', badge: 'Local Guide', quote: 'Everything was clean and well organized. The staff was friendly and helpful… such a fun and unique activity for friends or family. We’ll definitely be back!' },
  { name: 'Oliver Vasquez', quote: 'As a family of 5, this was a wonderful experience… 11/10 would recommend!' },
  { name: 'AdornedDc', badge: 'Local Guide', quote: '10 out of 10. Recommend. Staff were exceptionally kind. If you’re looking for something fun, safe yet educational, bring your kids!' },
  { name: 'Humza Khan', badge: 'Local Guide', quote: 'Great experience overall. My daughter loved it. The manager was really helpful.' },
  { name: 'Kimberly Willis', quote: 'Very friendly staff. Super cool experiences. My girls are loving the rides!' },
  { name: 'Mona Saliba', quote: 'Wow! The kids loved it! We are coming back again.' },
];

// Official Google "G" mark
const GoogleG: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
  </svg>
);

const BirthdayParties: React.FC = () => {
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState<number | null>(null);

  useEffect(() => {
    // Load Google Fonts
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Lemon&family=Fredoka:wght@300;400;500;600;700&family=Poppins:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  // Lightbox keyboard navigation + scroll lock
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') setLightbox((i) => (i === null ? i : (i + 1) % galleryItems.length));
      if (e.key === 'ArrowLeft') setLightbox((i) => (i === null ? i : (i - 1 + galleryItems.length) % galleryItems.length));
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox]);

  const showNext = () => setLightbox((i) => (i === null ? i : (i + 1) % galleryItems.length));
  const showPrev = () => setLightbox((i) => (i === null ? i : (i - 1 + galleryItems.length) % galleryItems.length));

  return (
    <main className="bg-gradient-to-br from-blue-50 via-purple-50 to-cyan-50 text-gray-800 overflow-hidden">
      {/* Hero Section with Birthday Table Images */}
      <section className="relative min-h-[500px] sm:min-h-[600px] lg:min-h-[700px] overflow-hidden">
        {/* Background Images with Overlay */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 opacity-65">
            <img 
              src="/assets/Images/birthday_table_1.jpeg" 
              alt="Birthday setup" 
              className="w-full h-full object-cover"
            />
            <img 
              src="/assets/Images/birthday_table_2.jpeg" 
              alt="Birthday celebration" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 via-purple-900/35 to-cyan-900/40"></div>
          {/* Floating Confetti Elements */}
          <motion.div
            className="absolute top-20 left-10 text-yellow-300 text-6xl"
            animate={{ y: [0, -20, 0], rotate: [0, 10, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            🎉
          </motion.div>
          <motion.div
            className="absolute top-40 right-20 text-cyan-300 text-5xl"
            animate={{ y: [0, 20, 0], rotate: [0, -10, 0] }}
            transition={{ duration: 2.5, repeat: Infinity }}
          >
            🎈
          </motion.div>
          <motion.div
            className="absolute bottom-20 left-1/4 text-blue-300 text-4xl"
            animate={{ y: [0, -15, 0], rotate: [0, 15, 0] }}
            transition={{ duration: 3.5, repeat: Infinity }}
          >
            ✨
          </motion.div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 text-center relative z-10">
          <motion.div
            className="inline-block mb-4"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, type: "spring" }}
          >
            <div className="bg-yellow-400 text-purple-800 px-6 py-2 rounded-full font-bold text-sm inline-block shadow-lg transform -rotate-2">
              🎂 THE ULTIMATE BIRTHDAY EXPERIENCE 🎂
            </div>
          </motion.div>

          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-4 sm:mb-6 text-white leading-tight px-2"
            style={{ fontFamily: "'Lemon', cursive", textShadow: '3px 3px 12px rgba(0,0,0,0.7), 0 0 30px rgba(0,0,0,0.5)' }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <span className="inline-block transform hover:scale-110 transition-transform">Immersa VR</span>
            <br />
            <span className="inline-block transform hover:scale-110 transition-transform text-cyan-300">Birthday Parties!</span>
          </motion.h1>
          
          <motion.p
            className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 text-white px-2"
            style={{ fontFamily: "'Fredoka', sans-serif", textShadow: '2px 2px 8px rgba(0,0,0,0.6)' }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2 }}
          >
            Where Every Kid Becomes a Hero! 🦸‍♂️🦸‍♀️
          </motion.p>
          
          <motion.p
            className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 text-cyan-200 font-medium px-4"
            style={{ fontFamily: "'Poppins', sans-serif", textShadow: '2px 2px 8px rgba(0,0,0,0.7)' }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4 }}
          >
            🎮 Epic VR Adventures • 🍕 Yummy Food • 🎉 Zero Stress for Parents
          </motion.p>
          
          <motion.div
            className="flex flex-col sm:flex-row justify-center gap-4 items-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.6 }}
          >
            <motion.a
              href="tel:+12272778291"
              className="bg-yellow-400 text-purple-800 font-bold py-3 sm:py-4 px-6 sm:px-10 rounded-full shadow-2xl hover:shadow-yellow-300/50 transform hover:scale-105 transition-all text-base sm:text-lg w-full sm:w-auto text-center"
              style={{ fontFamily: "'Fredoka', sans-serif" }}
              whileHover={{ rotate: [0, -2, 2, -2, 0] }}
              whileTap={{ scale: 0.95 }}
            >
              📞 Call to Book Your Party!
            </motion.a>
            <motion.a
              href="mailto:info@immersa.fun?subject=Birthday Party Booking Inquiry"
              className="bg-white text-purple-800 font-bold py-3 sm:py-4 px-6 sm:px-10 rounded-full shadow-2xl hover:shadow-white/50 transform hover:scale-105 transition-all text-base sm:text-lg w-full sm:w-auto text-center"
              style={{ fontFamily: "'Fredoka', sans-serif" }}
              whileHover={{ rotate: [0, 2, -2, 2, 0] }}
              whileTap={{ scale: 0.95 }}
            >
              ✉️ Email Us
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Google Reviews Trust Bar */}
      <section className="bg-white/80 backdrop-blur-sm border-y border-purple-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <motion.a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center group"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <GoogleG className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="text-yellow-400 text-lg sm:text-xl leading-none">★★★★★</span>
            <span className="font-bold text-gray-800 text-sm sm:text-base" style={{ fontFamily: "'Fredoka', sans-serif" }}>5.0</span>
            <span className="text-gray-600 text-sm sm:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Loved by <span className="font-semibold text-purple-600">100+ families</span> on Google
            </span>
            <span className="text-purple-600 text-sm font-semibold underline-offset-2 group-hover:underline" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Read reviews →
            </span>
          </motion.a>
        </div>
      </section>

      {/* Fun Stats Strip */}
      <section className="py-8 sm:py-12 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {[
              { icon: <Sparkles className="w-8 h-8" />, text: "Unique VR Worlds", color: "from-blue-400 to-purple-400", emoji: "🌟", borderColor: "border-blue-300" },
              { icon: <PartyPopper className="w-8 h-8" />, text: "Non-Stop Fun", color: "from-cyan-400 to-blue-400", emoji: "🎊", borderColor: "border-cyan-300" },
              { icon: <Pizza className="w-8 h-8" />, text: "Food Included", color: "from-orange-400 to-yellow-400", emoji: "🍕", borderColor: "border-orange-300" },
              { icon: <Shield className="w-8 h-8" />, text: "Safe & Clean", color: "from-green-400 to-teal-400", emoji: "✅", borderColor: "border-green-300" }
            ].map((item, index) => (
              <motion.div
                key={index}
                className={`relative bg-gradient-to-br ${item.color} p-4 sm:p-6 rounded-2xl sm:rounded-3xl shadow-2xl transform transition-all border-4 ${item.borderColor} ${index % 2 === 0 ? 'rotate-1 sm:rotate-2' : '-rotate-1 sm:-rotate-2'} hover:rotate-0`}
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ 
                  duration: 0.6, 
                  delay: index * 0.1,
                  type: "spring",
                  stiffness: 200
                }}
                viewport={{ once: true }}
                whileHover={{ 
                  y: -15, 
                  scale: 1.1,
                  rotate: 0,
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                }}
                whileTap={{ scale: 0.95 }}
              >
                {/* Animated background pulse */}
                <motion.div
                  className="absolute inset-0 rounded-2xl sm:rounded-3xl opacity-0"
                  animate={{
                    opacity: [0, 0.3, 0],
                    scale: [0.95, 1.05, 0.95]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.3
                  }}
                  style={{
                    background: `linear-gradient(to bottom right, rgba(255,255,255,0.5), transparent)`
                  }}
                />
                
                <div className="text-white text-center relative z-10">
                  <motion.div 
                    className="flex justify-center mb-3 text-5xl sm:text-6xl"
                    animate={{ 
                      rotate: [0, 10, -10, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 1,
                      delay: index * 0.2
                    }}
                  >
                    {item.emoji}
                  </motion.div>
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ delay: 0.3 + index * 0.1, type: "spring" }}
                  >
                    {item.icon}
                  </motion.div>
                  <motion.p 
                    className="mt-3 font-bold text-white text-base sm:text-lg drop-shadow-lg" 
                    style={{ fontFamily: "'Fredoka', sans-serif" }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                  >
                    {item.text}
                  </motion.p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Kids Go CRAZY Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white relative overflow-hidden">
        <motion.div
          className="absolute -top-20 -left-20 w-64 h-64 bg-pink-200 rounded-full opacity-50 blur-3xl"
          animate={{ scale: [1, 1.2, 1], x: [0, 50, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute -bottom-20 -right-20 w-64 h-64 bg-purple-200 rounded-full opacity-50 blur-3xl"
          animate={{ scale: [1, 1.3, 1], x: [0, -50, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        {/* Floating Emojis */}
        <motion.div
          className="absolute top-32 right-10 text-5xl"
          animate={{ y: [0, -25, 0], rotate: [0, 15, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
        >
          🎮
        </motion.div>
        <motion.div
          className="absolute bottom-32 left-16 text-4xl"
          animate={{ y: [0, 20, 0], rotate: [0, -12, 0] }}
          transition={{ duration: 3.5, repeat: Infinity }}
        >
          🚀
        </motion.div>
        <motion.div
          className="absolute top-1/2 right-20 text-3xl"
          animate={{ x: [0, 15, 0], rotate: [0, 10, 0] }}
          transition={{ duration: 5, repeat: Infinity }}
        >
          ⭐
        </motion.div>
        
        <div className="container mx-auto px-6 text-center relative z-10">
          <motion.h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-6 sm:mb-8 px-4"
            style={{ fontFamily: "'Fredoka', sans-serif" }}
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600">
              Why Kids Go CRAZY for Immersa!
            </span>{" "}
            <span className="inline-block">🤯</span>
          </motion.h2>
          
          <motion.p
            className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4"
            style={{ fontFamily: "'Poppins', sans-serif" }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Forget boring parties! At Immersa, kids don't just play games—they <span className="font-bold text-purple-600">become superheroes</span>, explore <span className="font-bold text-pink-600">magical worlds</span>, and create <span className="font-bold text-blue-600">unforgettable memories</span>!
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mt-8 sm:mt-12">
            {[
              { 
                title: "Epic VR Adventures", 
                description: "Fly through space, battle zombies, paint in 3D, and SO much more! Every kid gets 3-4 mind-blowing VR experiences.",
                icon: <Wand2 className="w-12 h-12" />,
                gradient: "from-blue-400 via-purple-400 to-cyan-400",
                emoji: "🚀"
              },
              { 
                title: "Your Private Party Zone", 
                description: "The ENTIRE VR arcade is yours! No strangers, no sharing—just pure fun with friends.",
                icon: <Users className="w-12 h-12" />,
                gradient: "from-cyan-400 via-blue-400 to-teal-400",
                emoji: "🎮"
              },
              { 
                title: "Parents Actually Relax!", 
                description: "We handle EVERYTHING—setup, hosting, food, cleanup. You just show up, take photos, and enjoy!",
                icon: <Heart className="w-12 h-12" />,
                gradient: "from-pink-400 via-rose-400 to-red-400",
                emoji: "😌"
              }
            ].map((card, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} rounded-3xl transform ${index % 2 === 0 ? 'rotate-3' : '-rotate-3'} group-hover:rotate-0 transition-transform duration-300`}></div>
                <div className="relative bg-white p-8 rounded-3xl shadow-xl transform group-hover:scale-105 transition-all">
                  <div className="text-6xl mb-4">{card.emoji}</div>
                  <div className={`text-white bg-gradient-to-br ${card.gradient} p-4 rounded-2xl inline-block mb-4`}>
                    {card.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {card.title}
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {card.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Real Party Gallery */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 relative overflow-hidden">
        <motion.div
          className="absolute -top-16 right-10 text-5xl opacity-30"
          animate={{ y: [0, -20, 0], rotate: [0, 15, 0] }}
          transition={{ duration: 4.4, repeat: Infinity }}
        >
          📸
        </motion.div>
        <motion.div
          className="absolute bottom-16 left-10 text-4xl opacity-30"
          animate={{ y: [0, 22, 0], rotate: [0, -14, 0] }}
          transition={{ duration: 3.9, repeat: Infinity }}
        >
          🎈
        </motion.div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-3 sm:mb-4 px-4"
            style={{ fontFamily: "'Fredoka', sans-serif" }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <span className="text-5xl sm:text-6xl">📸</span>{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600">
              Real Parties at Immersa
            </span>
          </motion.h2>
          <motion.p
            className="text-base sm:text-lg md:text-xl text-center text-gray-600 mb-8 sm:mb-12 max-w-2xl mx-auto px-4"
            style={{ fontFamily: "'Poppins', sans-serif" }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Not stock photos — these are actual celebrations, shared with our families' permission. Tap any photo or video to take a closer look. 👀
          </motion.p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 max-w-5xl mx-auto">
            {galleryItems.map((item, index) => (
              <motion.button
                key={index}
                type="button"
                onClick={() => setLightbox(index)}
                className="group relative aspect-square overflow-hidden rounded-2xl shadow-lg focus:outline-none focus:ring-4 focus:ring-purple-300"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.03 }}
                aria-label={`Open ${item.type} ${index + 1}: ${item.caption}`}
              >
                <img
                  src={item.type === 'video' ? item.poster : item.src}
                  alt={item.caption}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
                {item.type === 'video' && (
                  <>
                    <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full backdrop-blur-sm" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                      ▶ VIDEO
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-white/90 text-purple-700 rounded-full p-3 sm:p-4 shadow-xl transform group-hover:scale-110 transition-transform">
                        <Play className="w-5 h-5 sm:w-7 sm:h-7 fill-current" />
                      </div>
                    </div>
                  </>
                )}
                <p className="absolute bottom-0 left-0 right-0 p-2 sm:p-3 text-white text-[11px] sm:text-sm font-medium text-left leading-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {item.caption}
                </p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included - Fun Checklist Style */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-blue-100 via-purple-100 to-cyan-100 relative overflow-hidden">
        {/* Floating Party Elements */}
        <motion.div
          className="absolute top-20 left-10 text-5xl opacity-40"
          animate={{ y: [0, -20, 0], rotate: [0, 12, 0] }}
          transition={{ duration: 4.5, repeat: Infinity }}
        >
          🎁
        </motion.div>
        <motion.div
          className="absolute bottom-24 right-16 text-4xl opacity-40"
          animate={{ y: [0, 25, 0], rotate: [0, -15, 0] }}
          transition={{ duration: 3.8, repeat: Infinity }}
        >
          🎈
        </motion.div>
        <motion.div
          className="absolute top-1/3 right-12 text-3xl opacity-30"
          animate={{ x: [0, -20, 0], y: [0, 15, 0] }}
          transition={{ duration: 5.2, repeat: Infinity }}
        >
          🍕
        </motion.div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-8 sm:mb-12 px-4"
            style={{ fontFamily: "'Fredoka', sans-serif" }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <span className="text-5xl sm:text-6xl">🎁</span>{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600">
              What's in the Party Package?
            </span>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {[
              { text: "🍕 2 pizza slices + drink per kid (because adventures make you hungry!)", color: "from-orange-400 to-yellow-400" },
              { text: "🎂 Bring your own cake (we'll handle everything else!)", color: "from-purple-400 to-blue-400" },
              { text: "🎉 All party supplies—plates, cups, napkins & more!", color: "from-cyan-400 to-blue-400" },
              { text: "🙌 A full team of 5+ dedicated hosts running every minute of your party!", color: "from-blue-400 to-purple-400" },
              { text: "✨ We set up, we host, we clean up—you just have fun!", color: "from-teal-400 to-green-400" },
              { text: "🎮 120-minute premium experience including 60 minutes of private EPIC VR playtime", color: "from-purple-400 to-cyan-400" }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${item.color} rounded-2xl transform rotate-1 group-hover:rotate-0 transition-transform`}></div>
                <div className="relative bg-white p-6 rounded-2xl shadow-lg transform group-hover:scale-105 transition-all h-full flex items-center">
                  <p className="text-lg font-medium text-gray-800" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {item.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why We're Different - Fully Hosted, Service-Based */}
      <section className="py-12 sm:py-16 md:py-20 bg-white relative overflow-hidden">
        <motion.div
          className="absolute -top-20 right-0 w-72 h-72 bg-blue-100 rounded-full opacity-60 blur-3xl"
          animate={{ scale: [1, 1.2, 1], x: [0, -40, 0] }}
          transition={{ duration: 9, repeat: Infinity }}
        />
        <motion.div
          className="absolute -bottom-24 left-0 w-72 h-72 bg-purple-100 rounded-full opacity-60 blur-3xl"
          animate={{ scale: [1, 1.25, 1], x: [0, 40, 0] }}
          transition={{ duration: 11, repeat: Infinity }}
        />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-10 sm:mb-14"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="inline-block bg-purple-100 text-purple-700 px-4 py-1.5 rounded-full text-sm font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              🎯 NOT YOUR AVERAGE PARTY
            </div>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5"
              style={{ fontFamily: "'Fredoka', sans-serif" }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600">
                We Don't Hand You a Room and Walk Away
              </span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Most party places rent you a space and leave the entertaining to <span className="font-semibold text-purple-600">you</span>. Immersa is the opposite. Yours is a <span className="font-bold text-purple-600">fully-hosted, service-based party</span>: a team of <span className="font-bold text-blue-600">at least 5 hosts</span> runs the whole thing—leading every VR game, guiding smooth transitions, and even running activities at the table—so every single kid stays engaged from start to finish.
            </p>
          </motion.div>

          {/* Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12 sm:mb-16">
            <motion.div
              className="bg-gray-50 border-2 border-gray-200 rounded-3xl p-6 sm:p-8"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl sm:text-2xl font-bold text-gray-500 mb-5 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                😕 A Typical Party Place
              </h3>
              <ul className="space-y-3">
                {[
                  "Hands you a room, then disappears",
                  "You run the games & entertain the kids",
                  "You set up and clean up yourself",
                  "Shared space with strangers",
                  "Kids drift off and get bored",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-500" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    <X className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-300" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="relative rounded-3xl p-6 sm:p-8 shadow-2xl text-white overflow-hidden"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-purple-500 to-cyan-500" />
              <div className="relative z-10">
                <h3 className="text-xl sm:text-2xl font-bold mb-5 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  🎉 A Party at Immersa
                </h3>
                <ul className="space-y-3">
                  {[
                    "A team of 5+ hosts runs every single minute",
                    "We lead every game and every transition",
                    "Host-led table activities—not just \"sit and eat\"",
                    "The entire VR arcade, private—just your guests",
                    "Every kid stays engaged, start to finish",
                  ].map((t, i) => (
                    <li key={i} className="flex items-start gap-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-yellow-300" />
                      <span className="font-medium">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>

          {/* Service highlights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: <UserCheck className="w-9 h-9" />, stat: "5+", label: "Dedicated hosts", sub: "running games, transitions & table-time activities for your group", gradient: "from-blue-400 to-purple-400" },
              { icon: <Heart className="w-9 h-9" />, stat: "100%", label: "Hands-on the whole time", sub: "we keep every kid involved so no one gets left out or bored", gradient: "from-pink-400 to-rose-400" },
              { icon: <Sparkles className="w-9 h-9" />, stat: "1", label: "Private arcade, just yours", sub: "we close the whole space to other guests during your party", gradient: "from-cyan-400 to-blue-400" },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.12 }}
                viewport={{ once: true }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} rounded-3xl transform ${index % 2 === 0 ? 'rotate-2' : '-rotate-2'} group-hover:rotate-0 transition-transform`} />
                <div className="relative bg-white p-6 sm:p-8 rounded-3xl shadow-xl text-center h-full">
                  <div className={`text-white bg-gradient-to-br ${item.gradient} p-3 rounded-2xl inline-block mb-3`}>
                    {item.icon}
                  </div>
                  <div className={`text-4xl sm:text-5xl font-bold bg-gradient-to-r ${item.gradient} bg-clip-text text-transparent`} style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {item.stat}
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold text-gray-800 mt-1 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {item.label}
                  </h4>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {item.sub}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Value callout */}
          <motion.div
            className="max-w-3xl mx-auto mt-12 sm:mt-16 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-3xl p-6 sm:p-8 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="text-4xl mb-3">💚</div>
            <p className="text-base sm:text-lg md:text-xl text-gray-800 leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Here's the part parents love: even though we close the whole arcade and bring a full host team just for your group, you still pay a <span className="font-bold text-green-700">fraction</span> of what comparable private, fully-staffed experiences charge—often <span className="font-bold text-green-700">half, sometimes a third</span> of their price.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing - Colorful Cards */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* Animated Background Elements */}
        <motion.div
          className="absolute top-10 right-24 w-32 h-32 bg-blue-200 rounded-full opacity-30 blur-2xl"
          animate={{ scale: [1, 1.5, 1], y: [0, 30, 0] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-40 h-40 bg-cyan-200 rounded-full opacity-30 blur-2xl"
          animate={{ scale: [1, 1.4, 1], x: [0, -20, 0] }}
          transition={{ duration: 7, repeat: Infinity }}
        />
        {/* Floating Money Emojis */}
        <motion.div
          className="absolute top-32 left-16 text-4xl opacity-20"
          animate={{ y: [0, -30, 0], rotate: [0, 20, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
        >
          💵
        </motion.div>
        <motion.div
          className="absolute bottom-40 right-20 text-3xl opacity-20"
          animate={{ y: [0, 25, 0], rotate: [0, -18, 0] }}
          transition={{ duration: 4.5, repeat: Infinity }}
        >
          ✨
        </motion.div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.h2
            className="text-5xl sm:text-6xl font-bold text-center mb-8"
            style={{ fontFamily: "'Fredoka', sans-serif" }}
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <span className="inline-block">💰</span>{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-green-600 to-cyan-600">
              Simple Pricing, Amazing Value!
            </span>
          </motion.h2>
          
          <motion.p
            className="text-xl text-center text-gray-600 mb-12"
            style={{ fontFamily: "'Poppins', sans-serif" }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Up to 12 kids included • 2-hour fully-hosted party • 5+ hosts on your group!
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: "Weekends & Holidays",
                badge: "🎉 MOST POPULAR",
                times: [
                  { time: "9:30-11:30 AM", price: "$549", tag: "Early Bird!" },
                  { time: "11:30-1:30 PM", price: "$649", tag: "Prime Time!" },
                  { time: "6:00-8:00 PM", price: "$699", tag: "Evening Fun!" }
                ],
                gradient: "from-blue-500 to-purple-500"
              },
              {
                title: "Fridays",
                badge: "✨ GREAT DEAL",
                times: [
                  { time: "Before 4 PM", price: "$499", tag: "Best Value!" },
                  { time: "4:00-6:00 PM", price: "$549", tag: "After School!" },
                  { time: "6:00-8:00 PM", price: "$599", tag: "Prime Time!" }
                ],
                gradient: "from-cyan-500 to-blue-500"
              },
              {
                title: "Mon-Thursday",
                badge: "💎 BEST VALUE",
                times: [
                  { time: "Any 2 Hours", price: "$499", tag: "Super Deal!" }
                ],
                gradient: "from-green-500 to-emerald-500"
              }
            ].map((pricing, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${pricing.gradient} rounded-3xl transform ${index === 1 ? 'scale-105' : ''} opacity-90 blur-sm`}></div>
                {/* Animated pulse effect */}
                <motion.div
                  className="absolute inset-0 rounded-3xl opacity-0"
                  animate={{
                    opacity: [0, 0.2, 0],
                    scale: [0.95, 1.05, 0.95]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: index * 0.5
                  }}
                  style={{
                    background: `linear-gradient(to bottom right, rgba(255,255,255,0.5), transparent)`
                  }}
                />
                <div className="relative bg-white rounded-3xl shadow-2xl p-8 transform group-hover:scale-105 transition-all">
                  <div className={`bg-gradient-to-r ${pricing.gradient} text-white px-4 py-2 rounded-full text-sm font-bold inline-block mb-4 transform -rotate-2`}>
                    {pricing.badge}
                  </div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {pricing.title}
                  </h3>
                  <div className="space-y-4">
                    {pricing.times.map((slot, i) => (
                      <div key={i} className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-semibold text-gray-700" style={{ fontFamily: "'Poppins', sans-serif" }}>
                            {slot.time}
                          </span>
                          <span className={`text-2xl font-bold bg-gradient-to-r ${pricing.gradient} bg-clip-text text-transparent`} style={{ fontFamily: "'Fredoka', sans-serif" }}>
                            {slot.price}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">{slot.tag}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <p className="text-center text-gray-500 mt-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
            All prices in USD before tax • Up to 12 kids included
          </p>
          <p className="text-center text-gray-400 text-sm mt-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Prices subject to change • Terms apply (see below)
          </p>
        </div>
      </section>

      {/* Add-Ons - Fun extras */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-gray-100 via-blue-100 to-purple-100 relative overflow-hidden">
        {/* Floating Creative Elements */}
        <motion.div
          className="absolute top-16 right-24 text-5xl opacity-30"
          animate={{ y: [0, -22, 0], rotate: [0, 18, 0] }}
          transition={{ duration: 4.2, repeat: Infinity }}
        >
          🎨
        </motion.div>
        <motion.div
          className="absolute bottom-20 left-20 text-4xl opacity-30"
          animate={{ y: [0, 28, 0], rotate: [0, -20, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 3.6, repeat: Infinity }}
        >
          📸
        </motion.div>
        <motion.div
          className="absolute top-1/2 left-12 text-3xl opacity-25"
          animate={{ x: [0, 18, 0], rotate: [0, 15, 0] }}
          transition={{ duration: 5, repeat: Infinity }}
        >
          🦋
        </motion.div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.h2
            className="text-5xl sm:text-6xl font-bold text-center mb-12"
            style={{ fontFamily: "'Fredoka', sans-serif" }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <span className="inline-block">🌟</span>{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600">
              Make It Even MORE Special!
            </span>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { title: "Craft Activity", icon: <Palette className="w-10 h-10" />, emoji: "🎨", description: "Get creative with fun crafts!", gradient: "from-purple-400 to-blue-400" },
              { title: "Photo Experience", icon: <Camera className="w-10 h-10" />, emoji: "📸", description: "Capture every amazing moment!", gradient: "from-cyan-400 to-blue-400" },
              { title: "Face Painting", icon: <Sparkles className="w-10 h-10" />, emoji: "🦋", description: "Transform into anyone you want!", gradient: "from-teal-400 to-cyan-400" }
            ].map((addon, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, rotate: [0, -2, 2, 0] }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${addon.gradient} rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform`}></div>
                <div className="relative bg-white p-8 rounded-3xl shadow-xl text-center h-full items-center">
                  <div className="text-6xl mb-4">{addon.emoji}</div>
                  <div className={`text-white bg-gradient-to-br ${addon.gradient} p-4 rounded-2xl inline-block mb-4`}>
                    {addon.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {addon.title}
                  </h3>
                  <p className="text-gray-600 text-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {addon.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - Kids & Parents Love It */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* Floating Love Elements */}
        <motion.div
          className="absolute top-24 left-16 text-4xl opacity-20"
          animate={{ y: [0, -18, 0], rotate: [0, 12, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 3.5, repeat: Infinity }}
        >
          ❤️
        </motion.div>
        <motion.div
          className="absolute top-40 right-20 text-3xl opacity-20"
          animate={{ y: [0, 20, 0], x: [0, -10, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
        >
          ⭐
        </motion.div>
        <motion.div
          className="absolute bottom-32 left-24 text-4xl opacity-15"
          animate={{ y: [0, -25, 0], rotate: [0, -15, 0] }}
          transition={{ duration: 4.8, repeat: Infinity }}
        >
          💬
        </motion.div>
        <motion.div
          className="absolute bottom-40 right-16 text-3xl opacity-20"
          animate={{ y: [0, 22, 0], rotate: [0, 18, 0] }}
          transition={{ duration: 3.2, repeat: Infinity }}
        >
          😊
        </motion.div>
        {/* Animated gradient orbs */}
        <motion.div
          className="absolute top-1/4 right-32 w-24 h-24 bg-purple-200 rounded-full opacity-40 blur-2xl"
          animate={{ scale: [1, 1.3, 1], y: [0, 20, 0] }}
          transition={{ duration: 5, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 left-28 w-28 h-28 bg-blue-200 rounded-full opacity-40 blur-2xl"
          animate={{ scale: [1, 1.4, 1], x: [0, -15, 0] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <div className="container mx-auto px-6 relative z-10">
          <motion.h2
            className="text-5xl sm:text-6xl font-bold text-center mb-12"
            style={{ fontFamily: "'Fredoka', sans-serif" }}
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <span className="inline-block">❤️</span>{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600">
              What Parents Say
            </span>
          </motion.h2>

          {/* Featured Review */}
          <motion.div
            className="max-w-5xl mx-auto mb-12 sm:mb-16"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <div className="relative bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl shadow-2xl border-2 border-purple-100 overflow-hidden grid grid-cols-1 lg:grid-cols-5">
              <div className="lg:col-span-2 relative min-h-[240px] sm:min-h-[300px]">
                <img
                  src="/assets/BirthdayParties/party-table-wide.jpeg"
                  alt="Liev's 9th birthday party at Immersa"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/40 to-transparent" />
                <div className="absolute top-4 left-4 bg-yellow-400 text-purple-800 text-xs font-bold px-3 py-1 rounded-full" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  ⭐ FEATURED REVIEW
                </div>
              </div>
              <div className="lg:col-span-3 p-6 sm:p-8 md:p-10 relative">
                <Quote className="w-10 h-10 text-purple-200 mb-2" />
                <div className="text-2xl text-yellow-400 mb-4">⭐⭐⭐⭐⭐</div>
                <p className="text-lg sm:text-xl font-bold text-purple-700 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  "Immersa created an experience, not just a party."
                </p>
                <p className="text-gray-700 leading-relaxed mb-3 text-sm sm:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  We had my son Liev's 9th birthday at Immersa, and honestly, it went beyond what I'd hoped for. The kids had an absolute blast from start to finish—nonstop laughing and pure joy. This wasn't one of those venues where kids are turned loose while staff watch from a distance; the Immersa team was attentive, kind, and genuinely engaged the entire time.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3 text-sm sm:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Our host even put together a fun game during table time where Liev's friends answered silly, sweet questions about him—the kids loved it. The table setup was beautiful, everything was clean and organized, and the transition from gameplay to food and cake was seamless. I actually got to <span className="font-semibold text-purple-600">enjoy the party</span> instead of stressing over every detail.
                </p>
                <p className="text-gray-700 leading-relaxed mb-5 text-sm sm:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  I really think this will be one of the birthdays Liev remembers for a very long time. I'd absolutely recommend it to any family looking for something unique, interactive, and genuinely memorable.
                </p>
                <p className="font-bold text-purple-600" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  — Liev's Mom · 9th Birthday Party at Immersa
                </p>
              </div>
            </div>
          </motion.div>

          {/* Google Reviews social proof */}
          <motion.div
            className="max-w-3xl mx-auto text-center mb-8 sm:mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-3 bg-white border border-gray-200 rounded-full shadow-md px-5 py-2.5">
              <GoogleG className="w-6 h-6" />
              <span className="text-gray-700 font-semibold" style={{ fontFamily: "'Poppins', sans-serif" }}>Google</span>
              <span className="text-yellow-400 text-lg leading-none">★★★★★</span>
              <span className="font-bold text-gray-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>5.0</span>
            </div>
            <p className="text-base sm:text-lg text-gray-600 mt-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Immersa is rated five stars across <span className="font-bold text-purple-600">100+ reviews on Google</span> — here's what real families say.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 max-w-6xl mx-auto">
            {googleReviews.map((review, index) => (
              <motion.div
                key={index}
                className="relative bg-white p-6 rounded-2xl shadow-lg border border-gray-100 flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-yellow-400 text-lg leading-none">★★★★★</div>
                  <GoogleG className="w-5 h-5 opacity-90" />
                </div>
                <p className="text-gray-700 leading-relaxed mb-4 flex-grow text-sm sm:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  "{review.quote}"
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold flex-shrink-0" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {review.name.charAt(0)}
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-gray-800 text-sm leading-tight" style={{ fontFamily: "'Fredoka', sans-serif" }}>{review.name}</p>
                    {review.badge && <p className="text-xs text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>{review.badge}</p>}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <motion.a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white border-2 border-gray-200 hover:border-purple-300 text-gray-800 font-bold py-3 px-7 rounded-full shadow-lg hover:shadow-xl transition-all"
              style={{ fontFamily: "'Fredoka', sans-serif" }}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              <GoogleG className="w-5 h-5" />
              Read all our Google reviews
              <span aria-hidden="true">→</span>
            </motion.a>
          </div>
        </div>
      </section>

      {/* Final CTA - Big & Bold */}
      <section className="relative py-16 sm:py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-cyan-600"></div>
          <motion.div
            className="absolute top-0 left-0 w-full h-full"
            animate={{
              backgroundPosition: ['0% 0%', '100% 100%'],
            }}
            transition={{ duration: 20, repeat: Infinity, repeatType: 'reverse' }}
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '50px 50px',
            }}
          />
          {/* Large Floating Party Emojis */}
          <motion.div
            className="absolute top-20 left-10 text-6xl opacity-30"
            animate={{ y: [0, -30, 0], rotate: [0, 25, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
          >
            🎂
          </motion.div>
          <motion.div
            className="absolute top-32 right-16 text-5xl opacity-30"
            animate={{ y: [0, 35, 0], rotate: [0, -22, 0] }}
            transition={{ duration: 4.5, repeat: Infinity }}
          >
            🎊
          </motion.div>
          <motion.div
            className="absolute bottom-24 left-20 text-5xl opacity-30"
            animate={{ y: [0, -28, 0], x: [0, 15, 0] }}
            transition={{ duration: 4.8, repeat: Infinity }}
          >
            🎁
          </motion.div>
          <motion.div
            className="absolute bottom-32 right-24 text-4xl opacity-30"
            animate={{ y: [0, 32, 0], rotate: [0, 18, 0] }}
            transition={{ duration: 4.2, repeat: Infinity }}
          >
            🌟
          </motion.div>
          <motion.div
            className="absolute top-1/2 left-16 text-4xl opacity-25"
            animate={{ x: [0, -20, 0], y: [0, 20, 0] }}
            transition={{ duration: 5.5, repeat: Infinity }}
          >
            🚀
          </motion.div>
          <motion.div
            className="absolute top-1/3 right-20 text-3xl opacity-25"
            animate={{ x: [0, 15, 0], rotate: [0, -20, 0] }}
            transition={{ duration: 4.7, repeat: Infinity }}
          >
            ✨
          </motion.div>
        </div>

        <div className="container mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-8xl mb-6">🎉🎈🎊</div>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 sm:mb-8 leading-tight px-4"
              style={{ fontFamily: "'Fredoka', sans-serif" }}
            >
              Ready to Book the BEST<br />Birthday Party Ever?
            </h2>
            <p className="text-2xl sm:text-3xl text-cyan-200 mb-12 font-medium" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Call or email us today to reserve your party date! 🚀
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-6 items-center">
              <motion.a
                href="tel:+12272778291"
                className="bg-yellow-400 text-purple-800 font-bold py-4 sm:py-5 md:py-6 px-8 sm:px-10 md:px-12 rounded-full shadow-2xl hover:shadow-yellow-300/50 text-lg sm:text-xl md:text-2xl transform hover:scale-110 transition-all w-full sm:w-auto text-center"
                style={{ fontFamily: "'Fredoka', sans-serif" }}
                whileHover={{ rotate: [0, -3, 3, -3, 0] }}
                whileTap={{ scale: 0.95 }}
              >
                📞 CALL NOW: (227) 277-8291
              </motion.a>
              <motion.a
                href="mailto:info@immersa.fun?subject=Birthday Party Booking Inquiry&body=Hi! I'm interested in booking a VR birthday party at Immersa. Please send me more information!"
                className="bg-white text-purple-800 font-bold py-4 sm:py-5 md:py-6 px-8 sm:px-10 md:px-12 rounded-full shadow-2xl hover:shadow-white/50 text-base sm:text-lg md:text-xl transform hover:scale-110 transition-all w-full sm:w-auto text-center"
                style={{ fontFamily: "'Fredoka', sans-serif" }}
                whileHover={{ rotate: [0, 3, -3, 3, 0] }}
                whileTap={{ scale: 0.95 }}
              >
                ✉️ Email Us
              </motion.a>
            </div>

            <motion.p
              className="text-white text-lg mt-8"
              style={{ fontFamily: "'Poppins', sans-serif" }}
              animate={{ opacity: [1, 0.7, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              ⚡ Spots fill up fast! Don't miss out! ⚡
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Good to Know - Party Terms */}
      <section className="py-12 sm:py-16 bg-gray-50 border-t border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl sm:text-3xl font-bold text-center text-gray-700 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              📋 Good to Know — Party Terms
            </h3>
            <p className="text-center text-gray-500 mb-8 text-sm sm:text-base" style={{ fontFamily: "'Poppins', sans-serif" }}>
              A few simple things we'll confirm with you at booking so every party runs smoothly and safely.
            </p>
            <ul className="space-y-3">
              {[
                "Prices are subject to change and are confirmed at the time of booking. Quoted pricing covers up to 12 kids; additional guests, add-ons, food, and timing are arranged at booking and subject to availability.",
                "A deposit may be required to reserve your date and time. Rescheduling and cancellation terms are shared and agreed upon when you book.",
                "Immersa may pause, reschedule, or end a party in the event of circumstances beyond our control — including equipment or VR technical issues, power outages, or health and safety concerns. In these cases we'll work with you to reschedule or make it right.",
                "For everyone's safety, guests must follow our hosts' instructions and all posted safety guidelines. We reserve the right to pause or end the party — without refund — in cases of unsafe behavior, misuse of equipment, or disruptive or inappropriate conduct by guests.",
                "VR experiences carry age and health guidance; guests participate at their own discretion, and a signed waiver may be required. Guests are responsible for any damage caused by misuse of equipment or the venue.",
              ].map((term, index) => (
                <li key={index} className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <Shield className="w-5 h-5 mt-0.5 flex-shrink-0 text-purple-400" />
                  <span className="text-gray-600 text-sm sm:text-base leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {term}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-center text-gray-400 text-xs sm:text-sm mt-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Full terms are provided at booking. Questions? Just call or email us — we're happy to walk you through everything.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gradient-to-r from-gray-900 to-purple-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="mb-6">
            <div className="text-4xl mb-4">🎮✨🎉</div>
            <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Let's Make Magic Happen!
            </h3>
          </div>
          <div className="space-y-3 text-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <p>
              🌐 <a href="https://www.immersa.fun" className="text-yellow-400 hover:text-yellow-300 transition-colors font-semibold">www.immersa.fun</a>
            </p>
            <p>
              📧 <a href="mailto:info@immersa.fun" className="text-yellow-400 hover:text-yellow-300 transition-colors font-semibold">info@immersa.fun</a>
            </p>
            <p>
              📞 <a href="tel:+12272778291" className="text-yellow-400 hover:text-yellow-300 transition-colors font-semibold">+1 (227) 277-8291</a>
            </p>
          </div>
          <div className="mt-8 text-sm text-gray-400">
            Made with ❤️ for amazing kids and their awesome parents!
          </div>
        </div>
      </footer>

      {/* Gallery Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
          >
            {/* Close */}
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 sm:p-3 transition-colors z-10"
              aria-label="Close gallery"
            >
              <X className="w-6 h-6 sm:w-7 sm:h-7" />
            </button>

            {/* Prev */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); showPrev(); }}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 sm:p-3 transition-colors z-10"
              aria-label="Previous"
            >
              <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
            </button>

            {/* Next */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); showNext(); }}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 sm:p-3 transition-colors z-10"
              aria-label="Next"
            >
              <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
            </button>

            {/* Media */}
            <motion.div
              key={lightbox}
              className="relative max-w-3xl w-full flex flex-col items-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              {galleryItems[lightbox].type === 'video' ? (
                <video
                  src={galleryItems[lightbox].src}
                  poster={galleryItems[lightbox].poster}
                  controls
                  autoPlay
                  playsInline
                  className="max-h-[78vh] w-auto max-w-full rounded-2xl shadow-2xl bg-black"
                />
              ) : (
                <img
                  src={galleryItems[lightbox].src}
                  alt={galleryItems[lightbox].caption}
                  className="max-h-[78vh] w-auto max-w-full rounded-2xl shadow-2xl object-contain"
                />
              )}
              <p className="text-white text-center mt-4 text-base sm:text-lg font-medium px-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {galleryItems[lightbox].caption}
              </p>
              <p className="text-white/50 text-center mt-1 text-xs" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {lightbox + 1} / {galleryItems.length}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default BirthdayParties;
